/**
 * Native Interactive Site Map Feature Entry Point.
 *
 * Responsibilities:
 * - Inject Redux slices into the store
 * - Create and bootstrap the plugin host
 * - Manage map library loading and plugin lifecycle
 * - Export Redux actions and selectors for use in components
 */

import { epic$, store, store$ } from '@redux/store';
import { ReplaySubject, Subject, fromEvent } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import LoadingStatus from 'enums/loadingStatus';
import LayoutBreakpoint from 'layout/enums/layoutBreakpoint';
import {
  selectLoading,
  selectConfigurationResourceDomain,
  selectConfigurationImageResizeService,
  selectMapLibraryStatus,
  selectLayoutBreakpoint,
} from 'layout/redux/selectors';
import createMapLibraryPlugin from 'libraries/mapLibrary.plugin';
import uiStateSlice from 'layout/redux/uiStateSlice';
import modalService from 'layout/services/modalService';
import drawerService from 'layout/services/drawerService';
import createInteractiveSiteMapModalHandler from './handlers/modalHandler';
import { createMapLibraryLifecycleHandlers, getMapSourceUrl } from './handlers/mapLibraryLifecycleHandlers';
import createDrawerLifecycleHandlers from './handlers/drawerLifecycleHandlers';
import createMapControlsPlugin from './plugins/mapTypeToggle.plugin';
import createInteractiveSiteMapDrawerPlugin, { DRAWER_ID } from './plugins/drawer.plugin';
import createMapPlugin from './plugins/map.plugin';
import createPluginHost from './plugins/app';
import createRenderCommunitySiteMapPlugin from './plugins/renderCommunitySiteMap.plugin';
import createLotInteractionPlugin from './plugins/lotInteraction.plugin';
import createSiteMapViewportPlugin from './plugins/siteMapViewport.plugin';
import createMapChipsPlugin from './plugins/mapChips.plugin';
import createListViewPlugin from './plugins/listView.plugin';
import createLotMapSelectionPlugin from './plugins/lotMapSelection.plugin';
import createFloorPlanDetailPlugin from './plugins/floorPlanDetail.plugin';
import createHousingUnitDetailPlugin from './plugins/housingUnitDetail.plugin';
import createCardLotHighlightPlugin from './plugins/cardLotHighlight.plugin';
import createTrackingPlugin from './plugins/tracking.plugin';
import createImageErrorPlugin from './plugins/imageError.plugin';
import { createNativeInteractiveSiteMapDataSlice, DATA_SLICE_KEY } from './redux/dataSlice';

/**
 * Creates the modal and orchestrates Interactive Site Map initialization
 */
export default function setupNativeInteractiveSiteMap({ communityId, latitude, longitude, mapLibrarySlice } = {}) {
  const appRoot = document.querySelector('[data-native-interactive-site-map]');
  const triggerButton = document.querySelector('[data-show-native-interactive-site-map]');

  if (!appRoot && !triggerButton) {
    return;
  }

  // Inject Redux slices
  const dataSlice = createNativeInteractiveSiteMapDataSlice();
  store.injectReducer(DATA_SLICE_KEY, dataSlice.reducer);
  
  // A ReplaySubject(1) that emits once when runtime configuration becomes available.
  const configuration$ = new ReplaySubject(1);
  store$.pipe(
    filter(state => selectLoading(state) === LoadingStatus.Succeeded.toString()),
    map(state => ({
      resourceDomain: selectConfigurationResourceDomain(state),
      imageResizeService: selectConfigurationImageResizeService(state),
    })),
    take(1),
  ).subscribe(configuration$);

  // A ReplaySubject(1) that emits once when the map library has loaded successfully.
  const mapLibraryLoaded$ = new ReplaySubject(1);
  store$.pipe(
    map(selectMapLibraryStatus),
    filter(status => status === LoadingStatus.Succeeded.toString()),
    take(1),
  ).subscribe(mapLibraryLoaded$);

  const mapSourceElement = document.querySelector('[data-maps-script]');
  const mapSource = getMapSourceUrl(mapSourceElement);

  const mapLibraryLifecycle = createMapLibraryLifecycleHandlers(
    store.dispatch.bind(store),
    mapLibrarySlice.actions,
  );

  const drawerLifecycle = createDrawerLifecycleHandlers(
    store.dispatch.bind(store),
    uiStateSlice.actions,
    DRAWER_ID
  );

  let host = null;
  let beforeUnloadHandler = null;

  async function stopHost() {
    if (!host || typeof host.stop !== 'function') {
      return;
    }

    if (beforeUnloadHandler) {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      beforeUnloadHandler = null;
    }

    await host.stop();
    host = null;
  }

  async function bootstrapNativeInteractiveSiteMap(rootElement) {
    // Fresh per bootstrap (not shared across mobile drawer open/close cycles like
    // configuration$/mapLibraryLoaded$), so a reopen never replays a stale/detached
    // svgElement from a previous session.
    const svgOverlayReady$ = new ReplaySubject(1);

    // Plain Subject, not ReplaySubject — hover is "now or nothing", a late subscriber has nothing to catch up on.
    const cardLotHovered$ = new Subject();

    host = createPluginHost({
      root: rootElement,
      services: {
        epic$,
        store$,
        dispatch: store.dispatch.bind(store),
        getState: store.getState.bind(store),
        configuration$,
        drawerService,
        dataActions: dataSlice.actions,
        mapLibraryActions: mapLibrarySlice.actions,
        mapLibraryLoaded$,
        svgOverlayReady$,
        cardLotHovered$,
      },
      state: {
        community: { communityId, latitude, longitude },
        isMobile: selectLayoutBreakpoint(store.getState()) === LayoutBreakpoint.Small,
      },
      plugins: [
        createMapLibraryPlugin({
          provider: 'google',
          config: {
            src: mapSource,
            onScriptInjected: mapLibraryLifecycle.onMapLibraryFetch,
            onLoad: mapLibraryLifecycle.onMapLibraryLoaded,
            onError: mapLibraryLifecycle.onMapLibraryError,
          },
        }),
        createMapPlugin(),
        createMapControlsPlugin(),
        createRenderCommunitySiteMapPlugin(),
        createLotInteractionPlugin(),
        createSiteMapViewportPlugin(),
        createMapChipsPlugin(),
        createListViewPlugin(),
        createLotMapSelectionPlugin(),
        createFloorPlanDetailPlugin(),
        createHousingUnitDetailPlugin(),
        createCardLotHighlightPlugin(),
        createImageErrorPlugin(),
        createTrackingPlugin(),
        createInteractiveSiteMapDrawerPlugin(),
      ],
    });

    await host.init();
    await host.start();

    beforeUnloadHandler = () => stopHost();
    window.addEventListener('beforeunload', beforeUnloadHandler);
  }

  function createInteractiveSiteMapModal() {
    const modalTemplate = document.querySelector('[data-native-interactive-site-map-modal-template]');

    if (host || !modalTemplate) {
      return;
    }

    createInteractiveSiteMapModalHandler({
      appRoot: modalTemplate,
      modalService,
      dispatch: store.dispatch.bind(store),
      modalAction: uiStateSlice.actions.modalInformation,
      onReady: drawerLifecycle.onDrawerReady,
      onBootstrap: bootstrapNativeInteractiveSiteMap,
      onStop: async () => {
        drawerLifecycle.onDrawerClosed();
        store.dispatch(dataSlice.actions.viewReset());
        await stopHost();
      },
    });
  }

  if (triggerButton) {
    fromEvent(triggerButton, 'click').subscribe(createInteractiveSiteMapModal);
    return;
  }

  (async () => {
    await bootstrapNativeInteractiveSiteMap(appRoot);
  })();
}

// This feature is not responsible for tracking Segment nor GoogleAnalytics (or any other analytics).
// However, it exports the raw interaction Subjects, so the interested parties which own the actual dispatch
// of analytics events can subscribe to the Subjects and build their own events from the raw interaction data.
export {
  mapInteractionOccurred$,
  panelUpdatedOccurred$,
  panelScrollOccurred$,
  drawerDraggedOccurred$,
  lotSelectedOccurred$,
} from './redux/observables';

export {
  buildMapInteractionEvent as buildSegmentMapInteractionEvent,
  buildPanelUpdatedEvent as buildSegmentPanelUpdatedEvent,
  buildPanelScrollEvent as buildSegmentPanelScrollEvent,
  buildDrawerDraggedEvent as buildSegmentDrawerDraggedEvent,
  buildLotSelectedEvent as buildSegmentLotSelectedEvent,
} from './services/segmentEventBuilder';

export {
  buildMapInteractionEvent as buildGoogleAnalytics4MapInteractionEvent,
  buildPanelUpdatedEvent as buildGoogleAnalytics4PanelUpdatedEvent,
  buildPanelScrollEvent as buildGoogleAnalytics4PanelScrollEvent,
  buildDrawerDraggedEvent as buildGoogleAnalytics4DrawerDraggedEvent,
  buildLotSelectedEvent as buildGoogleAnalytics4LotSelectedEvent,
} from './services/googleAnalytics4EventBuilder';
